import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { Reveal, Magnetic, scrollTo } from "../lib/motion";
import { Kicker } from "./MidSections";
import { Logo } from "./Logo";
import LegalModal from "./LegalModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FinalContact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        ...form,
        lookup_key: "strategy_session",
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-[#5f6f80] outline-none transition-colors duration-300 focus:border-[#b8c0cb]/50";

  return (
    <section id="contact" className="relative overflow-hidden py-28 md:py-36" data-testid="contact-section">
      <div className="absolute left-1/2 top-0 h-[400px] w-[70%] -translate-x-1/2 bg-[#b8c0cb]/8 blur-[130px]" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 select-none justify-center opacity-[0.05]" aria-hidden="true">
        <span className="logo-metal font-display font-light tracking-[0.2em] text-[26vw] leading-none blur-[1px] md:text-[15vw]">SÛRSWA</span>
      </div>
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:grid-cols-2 md:px-10">
        <div>
          <Kicker id="final-kicker">Get started</Kicker>
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight md:text-6xl">
              The businesses winning tomorrow are building with AI today.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#8a99a8] md:text-lg">
              Book your 60-minute AI Strategy Session and discover exactly how intelligent
              automation can transform your business.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="strategy-price-card">
              <span className="text-metal font-display text-3xl font-semibold tracking-tight">$298</span>
              <span className="h-8 w-px bg-white/10" />
              <span className="text-sm leading-snug text-[#9aa2ac]">
                60-minute Strategy Session
                <br />
                <span className="text-[#e7eaee]">A focused deep-dive</span> into your biggest automation opportunities.
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col gap-4 text-sm text-[#8a99a8]">
              <a href="mailto:requests@surswa.com" className="flex items-center gap-3 transition-colors hover:text-white" data-testid="contact-email-link">
                <Mail className="h-4 w-4 text-[#b8c0cb]" /> requests@surswa.com
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="glass rounded-3xl p-7 md:p-9" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-4">
              <input data-testid="contact-name-input" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="contact-email-input" type="email" className={field} placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="contact-company-input" className={field} placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <textarea data-testid="contact-message-input" rows={4} className={`${field} resize-none`} placeholder="What would you like to automate?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Magnetic strength={0.2} className="mt-6 block">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-[#070708] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Book Session — $298 <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
              </button>
            </Magnetic>
            <p className="mt-4 text-center text-xs text-[#5f6f80]">Secure checkout via Stripe · 60-minute session · We reply within 24 hours.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

const FOOTER_COLS = [
  { h: "Company", links: ["Solutions", "Services", "Case Studies", "About"] },
  { h: "Resources", links: ["Business Growth Assessment", "AI Employees", "Privacy", "Terms"] },
  { h: "Connect", links: ["Contact"] },
];

export function Footer() {
  const [legal, setLegal] = useState(null);

  const handleLink = (label) => {
    if (label === "Privacy") setLegal("privacy");
    else if (label === "Terms") setLegal("terms");
    else scrollTo("#contact");
  };

  return (
    <footer className="border-t border-white/8 bg-[#0b0c0d] pt-20" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <button onClick={() => scrollTo("#top")} className="flex items-center" aria-label="SÛRSWA AI home">
              <Logo className="text-xl" />
            </button>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#8a99a8]">
              We build AI systems that become digital employees for businesses. The future of business, deployed today.
            </p>
          </div>
          {FOOTER_COLS.map((c) => (
            <div key={c.h} className="md:col-span-2">
              <h4 className="font-mono text-xs uppercase tracking-widest text-[#8a99a8]">{c.h}</h4>
              <ul className="mt-5 space-y-3">
                {c.links.map((l) => (
                  <li key={l}>
                    <button
                      onClick={() => handleLink(l)}
                      data-testid={`footer-link-${l.toLowerCase().replace(/\s/g, "-")}`}
                      className="text-sm text-white/70 transition-colors duration-300 hover:text-[#b8c0cb]"
                    >
                      {l}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-20 flex flex-col items-center justify-between gap-4 border-t border-white/8 py-8 text-xs text-[#5f6f80] md:flex-row">
          <span>© {new Date().getFullYear()} SÛRSWA AI. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <button onClick={() => setLegal("privacy")} className="transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy</button>
            <button onClick={() => setLegal("terms")} className="transition-colors hover:text-white" data-testid="footer-terms-link">Terms</button>
          </div>
        </div>
      </div>
      <LegalModal type={legal} onClose={() => setLegal(null)} />
    </footer>
  );
}
